// src/components/aiinsight/ReportBuilder.jsx
// Redesigned flat document-style report builder panel.
// Features:
//   - Flat document-style list (no card borders / colored backgrounds)
//   - Per-item checkboxes for multi-select
//   - Floating "Generate Report" action bar when items are selected
//   - Drag-grip reorder
//   - Inline title rename
//   - Remove per-item

import React, { useState, useCallback, useRef } from 'react';
import {
    Box, Typography, IconButton, Checkbox, Tooltip, Chip, Button,
    TextField, Divider, CircularProgress,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import BarChartIcon from '@mui/icons-material/BarChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AiChartRenderer from './AiChartRenderer';
import { markdownToHtml, isRawMarkdown } from './narrativeUtils';

// ── Single flat item row ──────────────────────────────────────────────────────
const FlatItem = ({
    item, idx,
    isSelected, onToggleSelect,
    onRemove, onRenameTitle,
    isDragOver,
    dragHandleProps,
}) => {
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleVal, setTitleVal] = useState(item.title || '');
    const [expanded, setExpanded] = useState(false);

    const isChart = !!item.viz_config_json;

    // Derive a short text preview
    const textPreview = (() => {
        const raw = item.narrative || '';
        if (!raw) return '';
        const plain = raw.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
        return plain.length > 90 ? plain.slice(0, 90) + '…' : plain;
    })();

    const commitTitle = () => {
        setEditingTitle(false);
        if (titleVal.trim() && titleVal.trim() !== item.title) {
            onRenameTitle(idx, titleVal.trim());
        }
    };

    return (
        <Box
            data-report-item-id={item.id}
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                py: 1.25,
                px: 0.75,
                borderRadius: 1.5,
                bgcolor: isSelected
                    ? 'rgba(99,102,241,0.06)'
                    : isDragOver
                        ? 'rgba(99,102,241,0.04)'
                        : 'transparent',
                border: isSelected
                    ? '1px solid rgba(99,102,241,0.25)'
                    : isDragOver
                        ? '1px dashed rgba(99,102,241,0.35)'
                        : '1px solid transparent',
                transition: 'all 0.15s',
                '&:hover': {
                    bgcolor: isSelected ? 'rgba(99,102,241,0.08)' : 'rgba(0,0,0,0.025)',
                    '& .item-actions': { opacity: 1 },
                },
                cursor: 'default',
                position: 'relative',
            }}
        >
            {/* Checkbox */}
            <Checkbox
                size="small"
                checked={isSelected}
                onChange={() => onToggleSelect(item.id)}
                sx={{
                    p: 0.25, mt: 0.2, flexShrink: 0,
                    color: 'rgba(99,102,241,0.4)',
                    '&.Mui-checked': { color: '#6366f1' },
                }}
            />

            {/* Drag grip */}
            <Box
                {...dragHandleProps}
                sx={{
                    cursor: 'grab', display: 'flex', alignItems: 'center',
                    color: 'text.disabled', flexShrink: 0, mt: 0.35,
                    '&:hover': { color: '#6366f1' },
                    '&:active': { cursor: 'grabbing' },
                }}
            >
                <DragIndicatorIcon sx={{ fontSize: 14 }} />
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Type chip */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <Chip
                        label={isChart ? 'Chart' : 'Insight'}
                        size="small"
                        sx={{
                            height: 14, fontSize: 8.5, fontWeight: 700, letterSpacing: 0.5,
                            bgcolor: isChart ? 'rgba(6,182,212,0.1)' : 'rgba(167,139,250,0.1)',
                            color: isChart ? '#06b6d4' : '#a78bfa',
                            border: 'none',
                        }}
                    />
                </Box>

                {/* Title — editable */}
                {editingTitle ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <TextField
                            autoFocus
                            size="small"
                            value={titleVal}
                            onChange={e => setTitleVal(e.target.value)}
                            onBlur={commitTitle}
                            onKeyDown={e => {
                                if (e.key === 'Enter') commitTitle();
                                if (e.key === 'Escape') { setTitleVal(item.title); setEditingTitle(false); }
                            }}
                            sx={{
                                flex: 1,
                                '& .MuiInputBase-input': { fontSize: 12.5, fontWeight: 600, py: 0.3 },
                                '& .MuiOutlinedInput-root': { borderRadius: 1 },
                            }}
                        />
                        <IconButton size="small" onClick={commitTitle} sx={{ color: 'success.main', p: 0.3 }}>
                            <CheckIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => { setTitleVal(item.title); setEditingTitle(false); }} sx={{ color: 'text.disabled', p: 0.3 }}>
                            <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                    </Box>
                ) : (
                    <Typography
                        sx={{
                            fontSize: 12.5, fontWeight: 600, color: 'text.primary',
                            lineHeight: 1.4, mb: 0.4,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {item.title || 'Untitled'}
                    </Typography>
                )}

                {/* Text preview */}
                {textPreview && !isChart && (
                    <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.5 }}>
                        {textPreview}
                    </Typography>
                )}

                {/* Chart mini-thumbnail */}
                {isChart && (
                    <Box sx={{ mt: 0.5, maxHeight: expanded ? 200 : 0, overflow: 'hidden', transition: 'max-height 0.25s ease' }}>
                        <AiChartRenderer
                            vizConfig={item.viz_config_json}
                            data={item.result_json || []}
                            height={160}
                            animationDisabled={true}
                        />
                    </Box>
                )}

                {/* Row count */}
                {item.result_json?.length > 0 && (
                    <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.25 }}>
                        {item.result_json.length} rows
                        {isChart && (
                            <Box
                                component="span"
                                onClick={() => setExpanded(e => !e)}
                                sx={{ ml: 1, cursor: 'pointer', color: '#818cf8', fontSize: 10, '&:hover': { textDecoration: 'underline' } }}
                            >
                                {expanded ? '▲ hide chart' : '▼ preview'}
                            </Box>
                        )}
                    </Typography>
                )}
            </Box>

            {/* Actions (hover reveal) */}
            <Box
                className="item-actions"
                sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }}
            >
                {!editingTitle && (
                    <Tooltip title="Rename">
                        <IconButton
                            size="small"
                            onClick={() => { setTitleVal(item.title || ''); setEditingTitle(true); }}
                            sx={{ p: 0.3, color: 'text.disabled', '&:hover': { color: '#6366f1' } }}
                        >
                            <EditOutlinedIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="Remove">
                    <IconButton
                        size="small"
                        onClick={() => onRemove(idx)}
                        sx={{ p: 0.3, color: 'text.disabled', '&:hover': { color: '#f43f5e' } }}
                    >
                        <DeleteOutlineIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};

// ── Main ReportBuilder ────────────────────────────────────────────────────────
const ReportBuilder = ({
    items,              // all pinned report items
    onRemove,           // (idx) => void
    onRenameTitle,      // (idx, newTitle) => void
    onReorder,          // (fromIdx, toIdx) => void
    onGenerateReport,   // (selectedIds) => void — triggers canvas editor
}) => {
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Drag state  
    const dragIdx = useRef(null);
    const dragOverIdx = useRef(null);
    const [dragOverIdxState, setDragOverIdxState] = useState(null);

    const toggleSelect = useCallback((id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const selectAll = () => setSelectedIds(new Set(items.map(i => i.id)));
    const clearAll = () => setSelectedIds(new Set());

    // Drag reorder
    const handleDragStart = (idx) => {
        dragIdx.current = idx;
        dragOverIdx.current = idx;
    };
    const handleDragEnter = (idx) => {
        dragOverIdx.current = idx;
        setDragOverIdxState(idx);
    };
    const handleDragEnd = () => {
        const from = dragIdx.current;
        const to = dragOverIdx.current;
        if (from !== null && to !== null && from !== to) {
            onReorder(from, to);
        }
        dragIdx.current = null;
        dragOverIdx.current = null;
        setDragOverIdxState(null);
    };

    const selectedCount = selectedIds.size;
    const hasItems = items.length > 0;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>

            {/* ── Header ── */}
            <Box sx={{
                px: 2, py: 1.5,
                borderBottom: '1px solid', borderColor: 'divider',
                display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0,
                bgcolor: 'background.paper',
            }}>
                <AutoAwesomeIcon sx={{ fontSize: 15, color: '#818cf8' }} />
                <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: 'text.primary', flex: 1 }}>
                    Report Builder
                </Typography>
                {hasItems && (
                    <Chip
                        label={items.length}
                        size="small"
                        sx={{ height: 16, fontSize: 10, bgcolor: 'rgba(99,102,241,0.12)', color: '#818cf8', border: 'none' }}
                    />
                )}
            </Box>

            {/* ── Multi-select toolbar ── */}
            {hasItems && (
                <Box sx={{
                    px: 1.5, py: 0.75,
                    display: 'flex', alignItems: 'center', gap: 1,
                    borderBottom: '1px solid', borderColor: 'divider',
                    bgcolor: t => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafafa',
                    flexShrink: 0,
                }}>
                    <Checkbox
                        size="small"
                        indeterminate={selectedCount > 0 && selectedCount < items.length}
                        checked={selectedCount === items.length}
                        onChange={selectedCount === items.length ? clearAll : selectAll}
                        sx={{
                            p: 0.25,
                            color: 'rgba(99,102,241,0.4)',
                            '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#6366f1' },
                        }}
                    />
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', flex: 1 }}>
                        {selectedCount > 0
                            ? `${selectedCount} of ${items.length} selected`
                            : 'Select items to generate report'}
                    </Typography>
                    {selectedCount > 0 && (
                        <Typography
                            onClick={clearAll}
                            sx={{ fontSize: 10.5, color: '#818cf8', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                            Clear
                        </Typography>
                    )}
                </Box>
            )}

            {/* ── Item list ── */}
            <Box sx={{
                flex: 1, overflowY: 'auto', px: 1, py: 1,
                display: 'flex', flexDirection: 'column', gap: 0.25,
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(99,102,241,0.2)', borderRadius: 2 },
                // Add bottom padding when the "Generate Report" bar is showing
                pb: selectedCount > 0 ? 9 : 1,
            }}>
                {!hasItems ? (
                    <Box sx={{
                        flex: 1, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        gap: 1.5, opacity: 0.45, py: 6, minHeight: 200,
                    }}>
                        <PushPinIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: 'text.primary', textAlign: 'center' }}>
                            No pinned items yet
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', textAlign: 'center', maxWidth: 200 }}>
                            Pin sections from chat results using the 📌 icon to build your report
                        </Typography>
                    </Box>
                ) : (
                    items.map((item, idx) => (
                        <Box
                            key={item.id || idx}
                            draggable
                            onDragStart={() => handleDragStart(idx)}
                            onDragEnter={() => handleDragEnter(idx)}
                            onDragEnd={handleDragEnd}
                            onDragOver={e => e.preventDefault()}
                        >
                            <FlatItem
                                item={item}
                                idx={idx}
                                isSelected={selectedIds.has(item.id)}
                                onToggleSelect={toggleSelect}
                                onRemove={onRemove}
                                onRenameTitle={onRenameTitle}
                                isDragOver={dragOverIdxState === idx && dragIdx.current !== idx}
                                dragHandleProps={{
                                    onMouseDown: () => { /* native drag handles it */ },
                                }}
                            />
                            {/* Thin divider between items */}
                            {idx < items.length - 1 && (
                                <Divider sx={{ mx: 1, borderColor: 'divider', opacity: 0.5 }} />
                            )}
                        </Box>
                    ))
                )}
            </Box>

            {/* ── Floating "Generate Report" bar ── */}
            {selectedCount > 0 && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0, left: 0, right: 0,
                        px: 1.5, py: 1.25,
                        bgcolor: '#ffffff',
                        borderTop: '1px solid rgba(99,102,241,0.2)',
                        boxShadow: '0 -4px 24px rgba(99,102,241,0.12)',
                        display: 'flex', alignItems: 'center', gap: 1,
                        zIndex: 20,
                        animation: 'slideUpBar 0.22s cubic-bezier(0.22,1,0.36,1)',
                        '@keyframes slideUpBar': {
                            from: { transform: 'translateY(100%)', opacity: 0 },
                            to: { transform: 'translateY(0)', opacity: 1 },
                        },
                    }}
                >
                    <Chip
                        label={`${selectedCount} selected`}
                        size="small"
                        sx={{
                            height: 20, fontSize: 10.5, fontWeight: 700,
                            bgcolor: 'rgba(99,102,241,0.12)', color: '#6366f1', border: 'none',
                        }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                        onClick={() => onGenerateReport(Array.from(selectedIds))}
                        sx={{
                            textTransform: 'none',
                            fontSize: 12.5,
                            fontWeight: 700,
                            borderRadius: 2,
                            px: 2,
                            background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                            boxShadow: '0 4px 16px rgba(99,102,241,0.4)',
                            '&:hover': { boxShadow: '0 6px 24px rgba(99,102,241,0.55)' },
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Generate Report →
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default ReportBuilder;
